import React, { useEffect, useState } from 'react';
import {
  Form,
  FormInstance,
  TextInput,
  Checkbox,
  SelectInputProps,
  FormValues,
  FieldArray
} from 'blueprint-form';
import {
  EpxSurgeryModuleConfig,
  EpxSurgeryPresetModule,
  IEpxSurgeryPreset,
  useGetAccountPresets,
  useGetModuleConfigs
} from '../../../../libs/apps/epxsurgery';
import { Label } from '@blueprintjs/core';
import { CardBlock, CardFrame, CardTitle } from '../../card';
import { LoadingBlock } from '../../loading/progress';
import { SimpleSelect } from '../../select';
import Tooltip from '../../tooltip';
import Modal from '../../modal';
import Toast from '../../toast';
import Callout from '../../callout';
import { Col, Row } from '../../../../grid';
import Button from '../../../molecules/button';
import Icon from '../../../atoms/icon';
import {
  faPlus,
  faQuestionCircle,
  faSave,
  faTrash
} from '@fortawesome/pro-solid-svg-icons';
import { ConfigComponent } from '../../../../libs/apps';
import { PostJSON, BaseResponse } from '../../../../libs/request';
import { useTerms } from '../../../../contexts/terms';

/**
 * Interfaces & utils
 */

interface IPresetModule extends FormValues, EpxSurgeryPresetModule {
  key: string;
  step?: number;
  isActive?: boolean;
}

interface PresetFormData {
  name: string;
  activeModules: IPresetModule[];
}

/**
 * Transforms an array of surgery presets
 * into select input options
 *
 * @param presets surgery presets
 * @returns select input options
 */
const createPresetOptions = (presets: IEpxSurgeryPreset[]) => {
  const options: SelectInputProps['options'] = [
    {
      label: '-- Select Your Preset --',
      value: ''
    }
  ];
  if (presets) {
    for (const preset of presets) {
      options.push({
        label: preset.name,
        value: preset.id
      });
    }
  }
  return options;
};

/**
 * Transforms an array of surgery preset modules
 * into select input options. Takes into consideration
 * whether the user is editing an existing preset,
 * and removing those preset module options from
 * the select input options
 *
 * @param modules surgery preset modules
 * @param presets surgery presets
 * @param editingId currently-being-edited preset id
 * @returns select input options
 */
const createModuleOptions = (
  modules: Record<string, EpxSurgeryModuleConfig>,
  presets: IEpxSurgeryPreset[],
  editingId: number
) => {
  const options: SelectInputProps['options'] = [
    {
      label: '-- Select A Module --',
      value: ''
    }
  ];
  if (modules) {
    const moduleKeys = Object.keys(modules);
    if (moduleKeys && moduleKeys.length) {
      for (const moduleKey in modules) {
        if (editingId !== null) {
          const preset = presets.find((preset) => preset.id === editingId);
          const presetModuleKeys = Object.keys(preset.modules);
          /**
           * Don't include options that are already selected
           * modules for the preset currently being edited
           */
          if (presetModuleKeys.includes(moduleKey)) {
            continue;
          }
        }
        options.push({
          label: modules[moduleKey].name,
          value: moduleKey
        });
      }
    }
  }
  return options;
};

/**
 * Creates the form data for the PresetForm
 * component. Handles if the user is creating
 * a new preset or if they're looking at
 * an existing preset.
 *
 * @param presets surgery presets
 * @param editingId currently-being-edited preset id
 * @returns PresetFormData
 */
const createFormData = (
  presets: IEpxSurgeryPreset[],
  editingId: number
): PresetFormData => {
  const formData: PresetFormData = {
    name: '',
    activeModules: []
  };
  /**
   * Given that we only use this function in a
   * component that won't render unless
   * editMode.isActive is true, if editingId
   * is not null then the user is editing an existing
   * preset. If it IS null, they're creating a new
   * preset and the default empty data above will suffice.
   */
  if (editingId !== null) {
    const preset = presets.find((preset) => preset.id === editingId);
    const activeModules = [];
    for (const moduleKey in preset.modules) {
      const presetModule = preset.modules[moduleKey];
      activeModules.push({
        ...presetModule,
        key: moduleKey
      });
    }
    formData.name = preset.name;
    formData.activeModules = activeModules;
  }
  return formData;
};

/**
 * ActiveModules component
 *
 * Displays the selected modules for a preset
 * and handles module removal and edits
 */

interface ActiveModulesProps {
  modules: IPresetModule[];
  addToOptions: (presetModule: IPresetModule) => void;
}

const ActiveModules: React.FC<ActiveModulesProps> = ({
  modules,
  addToOptions
}) => {
  if (!modules || !modules.length) {
    return null;
  }
  const removeModule = (
    removeFn: any,
    presetModule: IPresetModule,
    moduleIndex: number
  ) => {
    return () => {
      removeFn(moduleIndex);
      addToOptions(presetModule);
    };
  };
  return (
    <CardFrame>
      <CardBlock>
        <Row className="font-bold">
          <Col sm={6}>Selected Modules</Col>
          <Col sm={4}>
            <Tooltip content="The module's start date. (10 = 10 days after surgery) (-15 = 15 days before surgery)">
              Start <Icon icon={faQuestionCircle} />
            </Tooltip>
          </Col>
          <Col sm={4}>
            <Tooltip content="The module's end date. (10 = 10 days after surgery) (-15 = 15 days before surgery). To run indefinitely, leave blank.">
              End <Icon icon={faQuestionCircle} />
            </Tooltip>
          </Col>
          <Col sm={4}>
            <Tooltip content="Questions for this module will be asked every [step] days. (1 = every day, 2 = every other day, 7 = once per week)">
              Step <Icon icon={faQuestionCircle} />
            </Tooltip>
          </Col>
          <Col sm={4}>
            <Tooltip content="If module is not active, you will have the ability to turn it on at a later date.">
              Active <Icon icon={faQuestionCircle} />
            </Tooltip>
          </Col>
          <Col sm={2}>Actions</Col>
        </Row>
      </CardBlock>
      <FieldArray name="activeModules">
        {({ remove }) => (
          <React.Fragment>
            {modules.map((activeModule, index) => (
              <CardBlock key={activeModule.key}>
                <Row key={activeModule.key}>
                  <Col sm={6}>{activeModule.name}</Col>
                  <Col sm={4} className="form-group-mb-0">
                    <TextInput
                      aria-label={`${activeModule.name} Start`}
                      name={`activeModules[${index}].start`}
                    />
                  </Col>
                  <Col sm={4} className="form-group-mb-0">
                    <TextInput
                      aria-label={`${activeModule.name} End`}
                      name={`activeModules[${index}].end`}
                      className="mb-0"
                    />
                  </Col>
                  <Col sm={4} className="form-group-mb-0">
                    <TextInput
                      aria-label={`${activeModule.name} Step`}
                      name={`activeModules[${index}].step`}
                      className="mb-0"
                    />
                  </Col>
                  <Col sm={4}>
                    <Checkbox
                      aria-label={`${activeModule.name} Is Active`}
                      name={`activeModules[${index}].isActive`}
                      style={{
                        marginTop: '5px'
                      }}
                      large
                    />
                  </Col>
                  <Col sm={2}>
                    <Button
                      intent="danger"
                      onClick={removeModule(remove, activeModule, index)}
                    >
                      <Icon icon={faTrash} />
                    </Button>
                  </Col>
                </Row>
              </CardBlock>
            ))}
          </React.Fragment>
        )}
      </FieldArray>
    </CardFrame>
  );
};

/**
 * PresetForm component
 *
 * Handles creation and deletion of
 * surgery presets
 */

interface PresetFormProps {
  presets: IEpxSurgeryPreset[];
  defaultModules: Record<string, EpxSurgeryModuleConfig>;
  fetchPresets: () => void;
  resetEditMode: () => void;
  editingId: number;
}

const PresetForm: React.FC<PresetFormProps> = ({
  presets,
  defaultModules,
  fetchPresets,
  resetEditMode,
  editingId
}) => {
  const { terms } = useTerms();
  const [isLoading, setIsLoading] = useState(false);
  const [moduleOptions, setModuleOptions] = useState(
    createModuleOptions(defaultModules, presets, editingId)
  );
  const initialFormData = createFormData(presets, editingId);
  const form = new FormInstance<PresetFormData>(initialFormData);
  const isSelectDisabled = !defaultModules || moduleOptions.length === 1;
  /**
   * Update module options once
   * default modules have loaded
   */
  useEffect(() => {
    setModuleOptions(createModuleOptions(defaultModules, presets, editingId));
  }, [defaultModules === null]);
  /**
   * Handles if the user switches between
   * two different existing presets, or
   * they click the "Create New" button
   * while editing an existing preset
   */
  useEffect(() => {
    const formData = createFormData(presets, editingId);
    form.setData(formData);
  }, [editingId]);
  /**
   * Save preset
   */
  const savePreset = (data: PresetFormData) => {
    if (isLoading) {
      return;
    }
    if (!data.name) {
      return;
    }
    if (!data.activeModules || !data.activeModules.length) {
      return;
    }
    const presetModules = {};
    for (const presetModule of data.activeModules) {
      presetModules[presetModule.key] = presetModule;
    }
    const params = {
      name: data.name,
      modules: presetModules
    };
    setIsLoading(true);
    PostJSON<BaseResponse>({
      url: '/epxsurgery/create_preset/',
      params
    }).then((response) => {
      setIsLoading(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Creating Preset',
          content: response.message
        });
        return;
      }
      Toast({
        intent: 'success',
        message: 'The preset has been created successfully!'
      });
      fetchPresets();
    });
  };
  /**
   * Delete preset
   */
  const deletePreset = () => {
    if (!editingId) {
      return;
    }
    Modal.confirm({
      title: 'Delete Custom Preset',
      content:
        'Are you sure you want to delete this custom preset? This action is not reversible.',
      onOk: () => {
        setIsLoading(true);
        PostJSON<BaseResponse>({
          url: '/epxsurgery/delete_preset/',
          params: {
            id: editingId
          }
        }).then((response) => {
          setIsLoading(false);
          if (response.status !== 'OK') {
            Modal.error({
              title: 'Error Deleting Preset',
              content: response.message
            });
            return;
          }
          Toast({
            intent: 'success',
            message: 'The preset has been deleted successfully!'
          });
          fetchPresets();
          resetEditMode();
        });
      }
    });
  };
  /**
   * Handle selecting a module
   * from module options
   */
  const selectModule = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const moduleKey = event.target.value;
    const defaultModule = defaultModules[moduleKey];
    const presetModule: IPresetModule = {
      ...defaultModule,
      key: moduleKey,
      isActive: true,
      step: 1
    };
    // Add to form active modules
    const currentActiveModules = form.getForm().values.activeModules;
    form.setFieldValue(
      'activeModules',
      currentActiveModules.concat(presetModule)
    );
    // Remove from available options
    setModuleOptions((opts) => opts.filter((opt) => opt.value !== moduleKey));
  };
  /**
   * Handle re-adding a module
   * to the options upon module deletion
   */
  const addToOptions = (presetModule: IPresetModule) => {
    const option = {
      label: presetModule.name as string,
      value: presetModule.key
    };
    setModuleOptions((opts) => opts.concat(option));
  };
  /**
   * Show loading state if modules
   * are still loading
   */
  if (defaultModules === null) {
    return <LoadingBlock />;
  }
  return (
    <Form form={form} onSubmit={savePreset}>
      {({ values }) => (
        <React.Fragment>
          {editingId !== null ? (
            <Callout intent="primary" className="my-6">
              Since presets may have been prescribed to {terms.patient.plural},
              we do not allow users to update them. If you'd like to update this
              preset, please create a new one by updating the name below and
              selecting your updated settings.
            </Callout>
          ) : null}
          <Row>
            <Col>
              <TextInput
                label="Preset Name"
                name="name"
                placeholder="My New Preset"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Label>
                Available Modules
                <SimpleSelect
                  name="preset"
                  options={moduleOptions}
                  disabled={isSelectDisabled}
                  onChange={selectModule}
                  fill
                />
              </Label>
            </Col>
          </Row>
          <ActiveModules
            modules={values.activeModules}
            addToOptions={addToOptions}
          />
          <Row>
            <Col md={6}>
              <Button
                intent="primary"
                type="submit"
                className="mt-6"
                loading={isLoading}
                fill
              >
                <Icon icon={faSave} className="mr-4" />
                Save Preset
              </Button>
            </Col>
            {editingId !== null ? (
              <Col md={6}>
                <Button
                  intent="danger"
                  className="mt-6"
                  loading={isLoading}
                  onClick={deletePreset}
                  fill
                >
                  <Icon icon={faTrash} className="mr-4" />
                  Delete Preset
                </Button>
              </Col>
            ) : null}
          </Row>
        </React.Fragment>
      )}
    </Form>
  );
};

/**
 * Surgery Custom Presets UI component
 *
 * For adding, deleting, and creating custom
 * Surgery presets
 *
 * @returns JSX.Element
 */
export const EpxSurgeryConfigForm: ConfigComponent = () => {
  const [editMode, setEditMode] = useState({
    editingId: null,
    isActive: false
  });
  const { presets, run: fetchPresets } = useGetAccountPresets();
  const defaultModules = useGetModuleConfigs();
  const presetOptions = createPresetOptions(presets);
  const presetSelectValue =
    editMode.editingId === null ? '' : editMode.editingId;

  const header = (
    <CardTitle>
      <h4>Custom Presets</h4>
    </CardTitle>
  );

  // Presets still loading
  if (presets === null) {
    return (
      <React.Fragment>
        {header}
        <CardBlock>
          <LoadingBlock />
        </CardBlock>
      </React.Fragment>
    );
  }

  const handleCreateNew = () => {
    setEditMode({
      editingId: null,
      isActive: true
    });
  };

  const handleEditExisting = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const id = parseInt(event.target.value, 10);
    setEditMode({
      editingId: id ? id : null,
      isActive: id ? true : false
    });
  };

  const resetEditMode = () => {
    setEditMode({
      editingId: null,
      isActive: false
    });
  };

  return (
    <React.Fragment>
      {header}
      <CardBlock>
        <Row>
          <Col lg={15}>
            <SimpleSelect
              name="preset"
              options={presetOptions}
              disabled={presets.length === 0}
              onChange={handleEditExisting}
              value={presetSelectValue}
              fill
            />
          </Col>
          <Col lg={9}>
            <Button intent="primary" onClick={handleCreateNew} fill>
              <Icon icon={faPlus} className="mr-2" />
              Create Preset
            </Button>
          </Col>
        </Row>
        {editMode.isActive ? (
          <div className="mt-6">
            <PresetForm
              presets={presets}
              defaultModules={defaultModules}
              fetchPresets={fetchPresets}
              editingId={editMode.editingId}
              resetEditMode={resetEditMode}
            />
          </div>
        ) : null}
      </CardBlock>
    </React.Fragment>
  );
};
